import React from "react";
import { z } from "zod";

// components
import { FormCheckboxGroup, FormDatePicker, FormField, PanelFilters } from "@performyard/py-components";

import { MultipleSelectEmployee, SingleSelectEmployee } from "../../../../../../../components/Inputs/SelectEmployee";
import { IReviewForm, SingleSelectForm } from "../../../../../../../components/Inputs/SelectForm";
import { Option } from "../../../../../../../components/Inputs/SelectionOptions/Checkboxes";

export const StatusSelectionOptions: Option[] = [
	{
		label: "In Progress",
		value: "in_progress",
	},
	{
		label: "Overdue",
		value: "overdue",
	},
	{
		label: "Scheduled",
		value: "scheduled",
	},
	{
		label: "Completed",
		value: "completed",
	},
];

export const ShowOnlyFormsWithUnassignedAuthorsOptions: Option[] = [
	{
		label: "Show only forms with unassigned authors",
		value: "yes",
	},
];

const AllFormsFiltersSchema = z.object({
	subject: z
		.object({
			id: z.string(),
			name: z.string(),
		})
		.optional()
		.nullable(),
	form: z
		.object({
			_id: z.string(),
			name: z.string(),
			deleted_at: z.string().optional(),
		})
		.optional()
		.nullable(),
	showOnlyExternalForms: z.array(z.string()).optional(),
	includeDeactivatedSubjects: z.array(z.string()).optional(),
	authors: z
		.array(
			z.object({
				id: z.string(),
				name: z.string(),
			})
		)
		.optional(),
	showOnlyFormsWithUnassignedAuthors: z.array(z.string()).optional(),
	signers: z
		.array(
			z.object({
				id: z.string(),
				name: z.string(),
			})
		)
		.optional(),
	authorsDueDateAfter: z.date().nullish(),
	authorsDueDateBefore: z.date().nullish(),
	signersDueDateAfter: z.date().nullish(),
	signersDueDateBefore: z.date().nullish(),
	completedDateBefore: z.date().nullish(),
	completedDateAfter: z.date().nullish(),
	status: z.array(z.string()).optional(),
	redacted: z.boolean().optional(),
});

export type AllFormsFilter = z.infer<typeof AllFormsFiltersSchema>;

interface AllFormsFiltersProps {
	isOpen: boolean;
	onClose: () => void;
	filters: AllFormsFilter;
	onApplyFilters: (filters: AllFormsFilter) => void;
}

const AllFormsFilters = ({ isOpen, onClose, filters, onApplyFilters }: AllFormsFiltersProps) => {
	const defaultValues: AllFormsFilter = {
		status: filters?.status ?? [],
		subject: filters?.subject,
		form: filters?.form,
		showOnlyExternalForms: filters?.showOnlyExternalForms ?? [],
		includeDeactivatedSubjects: filters?.includeDeactivatedSubjects ?? [],
		authors: filters?.authors ?? [],
		showOnlyFormsWithUnassignedAuthors: filters?.showOnlyFormsWithUnassignedAuthors ?? [],
		signers: filters?.signers ?? [],
		authorsDueDateAfter: filters?.authorsDueDateAfter,
		authorsDueDateBefore: filters?.authorsDueDateBefore,
		signersDueDateAfter: filters?.signersDueDateAfter,
		signersDueDateBefore: filters?.signersDueDateBefore,
		completedDateBefore: filters?.completedDateBefore,
		completedDateAfter: filters?.completedDateAfter,
		redacted: filters?.redacted,
	};

	const handleApplyFilters = ({
		value,
	}: {
		value: Partial<AllFormsFilter>;
		resetForm: () => void;
		setError: (field: keyof AllFormsFilter, error: string) => void;
	}) => {
		const formattedState = { ...value };
		Object.keys(formattedState).forEach((key) => {
			const filterValue = formattedState[key as keyof AllFormsFilter];
			if (
				filterValue === null ||
				filterValue === undefined ||
				(Array.isArray(filterValue) && filterValue.length === 0)
			) {
				delete formattedState[key as keyof AllFormsFilter];
			}
		});

		onApplyFilters(formattedState);
		onClose();
	};

	return (
		<PanelFilters
			isOpen={isOpen}
			onClose={onClose}
			schema={AllFormsFiltersSchema}
			defaultValues={defaultValues}
			onSubmit={handleApplyFilters}
		>
			<div className="space-y-4">
				<FormCheckboxGroup id="status" label="Form Status" options={StatusSelectionOptions} />
				<FormField<{ id: string; name: string } | null>
					id="subject"
					label="Subject"
					input={(field) => (
						<SingleSelectEmployee
							placeholder="Make a selection"
							value={field.value}
							onChange={(employeeId, employeeName) => {
								field.onChange(
									employeeId && employeeName ? { id: employeeId, name: employeeName } : null
								);
							}}
						/>
					)}
				/>
				<FormField<IReviewForm | null>
					id="form"
					label="Form"
					input={(field) => (
						<SingleSelectForm
							clearable={true}
							placeholder="Enter form name..."
							value={field.value || undefined}
							onChange={(value) => field.onChange(value ?? null)}
						/>
					)}
				/>
				<FormCheckboxGroup
					id="showOnlyExternalForms"
					label=""
					options={[
						{
							label: "Show only external forms",
							value: "yes",
						},
					]}
				/>
				<FormCheckboxGroup
					id="includeDeactivatedSubjects"
					label=""
					options={[
						{
							label: "Include deactivated subjects",
							value: "yes",
						},
					]}
				/>
				<FormField<{ id: string; name: string }[]>
					id="authors"
					label="Authors"
					input={(field) => (
						<MultipleSelectEmployee
							placeholder="Make a selection"
							value={field.value || []}
							onChange={field.onChange}
						/>
					)}
				/>
				<FormCheckboxGroup
					id="showOnlyFormsWithUnassignedAuthors"
					label=""
					options={ShowOnlyFormsWithUnassignedAuthorsOptions}
				/>
				<FormField<{ id: string; name: string }[]>
					id="signers"
					label="Signers"
					input={(field) => (
						<MultipleSelectEmployee
							placeholder="Make a selection"
							value={field.value || []}
							onChange={field.onChange}
						/>
					)}
				/>
				<FormDatePicker id="authorsDueDateAfter" label="Author's Due Date After" clearable={true} />
				<FormDatePicker id="authorsDueDateBefore" label="Author's Due Date Before" clearable={true} />
				<FormDatePicker id="signersDueDateAfter" label="Signer's Due Date After" clearable={true} />
				<FormDatePicker id="signersDueDateBefore" label="Signer's Due Date Before" clearable={true} />
				<FormDatePicker id="completedDateAfter" label="Completed Date After" clearable={true} />
				<FormDatePicker id="completedDateBefore" label="Completed Date Before" clearable={true} />
			</div>
		</PanelFilters>
	);
};

export default AllFormsFilters;
